import { Avatar, Box, Button, Divider, Grid, Stack, Typography, type SxProps } from "@mui/material";
import React, { useEffect } from "react";
import { Bolt, TrendingUp } from "@mui/icons-material";
import CheckoutButton from "../cartMgmt/CheckoutButton.tsx";
import ProductManager from "../../lib/Product/ProductManager.ts";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Tooltip from "../Tooltip/Tooltip.tsx";
import type { SelectedProducts } from "../../types/product";
import { createLogger } from "../../lib/logger.ts";
import { useAddressData } from "../../context/FunnelDataContext.tsx";
import { FormattedNumber } from "react-intl";

const logger = createLogger("CostsCalculation");

type Props = {
    moduleCount: number;
    selectedProducts: SelectedProducts;
    selectedAppointment?: string | null;
    kwPerYearAndModule: number;
    powerConsumption: number;
    costsPerKwh: number;
};

type CostsItemProps = {
    value: string | React.ReactNode;
    displayText: string | React.ReactNode;
    icon: React.ReactNode;
};

const CostsItem: React.FC<CostsItemProps> = ({ value, displayText, icon }) => {
    return (
        <Stack direction={{ xs: "row" }} alignItems="center">
            <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ backgroundColor: "var(--mui-palette-background-default)" }}>
                    {icon}
                </Avatar>
                <Typography>{displayText}</Typography>
            </Stack>
            <Box sx={{ flexGrow: 1 }}/>
            <Typography variant={"h4"}>
                {value}
            </Typography>
        </Stack>
    );
}

const CostsCalculation: React.FC<Props> = ({
    moduleCount,
    selectedProducts,
    selectedAppointment,
    kwPerYearAndModule,
    powerConsumption,
    costsPerKwh
}) => {
    const theme = useTheme();
    const paperStyle: SxProps = {
        backgroundColor: "#1a1c2f",
        borderRadius: "10px 0px 0px 0px",
        boxShadow: "0px 5px 7.5px rgba(0,0,0,0.16)",
        p: 3,
        alignContent: "center",
        alignItems: 'stretch',
    };

    const [open, setOpen] = React.useState(false);
    const addressData = useAddressData()!;

    const [summaryStore, setSummaryStore] = React.useState(ProductManager.getCostsCalculation(selectedProducts, moduleCount, kwPerYearAndModule, powerConsumption, costsPerKwh, addressData.currentAddress));
    const [financingTable, setFinancingTable] = React.useState(ProductManager.getFinancingTable(summaryStore));

    useEffect(() => {
        setSummaryStore(ProductManager.getCostsCalculation(selectedProducts, moduleCount, kwPerYearAndModule, powerConsumption, costsPerKwh, addressData.currentAddress));
        setFinancingTable(ProductManager.getFinancingTable(summaryStore));

        logger.debug('Updated Costs Calculation', { selectedProducts, moduleCount, summaryStore, financingTable });
    }, [selectedProducts, moduleCount, kwPerYearAndModule, powerConsumption, costsPerKwh, addressData.currentAddress]);

    const handlePriceClick = () => {
        setOpen(!open);
    }

    return (
        <Grid container spacing={0}>
            {/* Left Section - Savings */}
            <Grid
                size={{ xs: 12, md: 7 }} sx={{
                ...paperStyle,
                backgroundColor: "#1a1c2f",
                borderRadius: { md: "10px 0px 0px 0px" },
                boxShadow: { md: "0px 5px 7.5px rgba(0,0,0,0.16)" },
                pl: { xs: 2, md: 6, xl: 12 }
            }}
            >
                <Stack
                    direction={{ xs: "column", md: "row" }} spacing={3} divider={
                    <Divider
                        orientation={useMediaQuery(theme.breakpoints.down("md")) ? "horizontal" : "vertical"}
                        sx={{ borderColor: 'var(--mui-palette-primary-contrastText)' }}
                        flexItem
                    />
                } sx={{ alignItems: "center", color: 'var(--mui-palette-primary-contrastText)' }}
                >
                    <Grid size={{ xs: 12, md: 6 }}>
                        <CostsItem
                            value={
                                <FormattedNumber
                                    value={Math.round((summaryStore.economy?.amortizationPeriod || 0) * 2) / 2}
                                    minimumFractionDigits={0}
                                    maximumFractionDigits={1}
                                    style={"unit"}
                                    unitDisplay={"long"}
                                    unit="year"
                                />
                            }
                            displayText={<span>Amortisationszeit: <Tooltip
                                text={"Die Amortisationszeit zeigt, nach wie vielen Jahren sich die Investition in Ihre PV-Anlage voraussichtlich bezahlt gemacht hat. Dafür werden die Investitionskosten durch die Summe aus jährlicher Stromkostenersparnis und Einspeisevergütung geteilt. Je kürzer die Amortisationszeit, desto schneller rechnet sich Ihre Anlage."}/></span>}
                            icon={<TrendingUp sx={{ width: 35, height: 35, fill: "#041B2B" }}/>}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <CostsItem
                            value={<FormattedNumber
                                value={summaryStore.economy?.yearlySavings ? summaryStore.economy.yearlySavings / 12 : 0}
                                style={"currency"}
                                currency={"EUR"}
                            />}
                            displayText="Monatliche Stromersparnis:"
                            icon={<Bolt sx={{ width: 35, height: 35, fill: "#041B2B" }}/>}
                        />
                    </Grid>
                </Stack>
            </Grid>

            {/* Right Section - Pricing */}
            <Grid size={{ xs: 12, md: 5 }}>
                <Stack
                    sx={{
                        ...paperStyle,
                        backgroundColor: "#00031f",
                        borderRadius: { md: "0px 10px 0px 0px" },
                        color: 'var(--mui-palette-primary-contrastText)',
                        pr: { xs: 2, md: 6, xl: 12 }
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Stack alignItems={"center"}>
                            <Typography variant={"h4"} onClick={handlePriceClick} noWrap sx={{ cursor: 'pointer' }}>
                                <FormattedNumber
                                    value={financingTable[0].monthlyPaymentIncludingFees}
                                    style="currency"
                                    currency="EUR"
                                /> mtl.*
                            </Typography>
                            {open && (
                                <Typography fontSize={'.75rem'}>* für {financingTable[0].term * 12} Monate</Typography>
                            )}
                            <Typography noWrap>
                                <FormattedNumber
                                    value={summaryStore.economy?.totalCost || 0}
                                    style="currency"
                                    currency="EUR"
                                /> Kaufpreis
                            </Typography>
                        </Stack>
                        <Button
                            onClick={handlePriceClick}
                            sx={{ alignSelf: 'baseline', minWidth: 'auto', p: 0, pt: { xs: 1, md: 0 } }}
                        >
                            {open ?
                                <ExpandLessIcon
                                    fontSize={'medium'}
                                    htmlColor={'var(--mui-palette-primary-contrastText)'}
                                />
                                :
                                <ExpandMoreIcon
                                    fontSize={'medium'}
                                    htmlColor={'var(--mui-palette-primary-contrastText)'}
                                />
                            }
                        </Button>
                        <CheckoutButton
                            moduleCount={moduleCount}
                            selectedProducts={selectedProducts}
                            pickedDate={selectedAppointment || null}
                            kwPerYearAndModule={kwPerYearAndModule}
                            powerConsumption={powerConsumption}
                            costsPerKwh={costsPerKwh}
                        />
                    </Stack>
                </Stack>
            </Grid>
        </Grid>
    );
};

export default CostsCalculation;
